import type { AgentGateway } from '@mummssoftware/utils';

import { REQUEST_TIMEOUT_MS } from '../constants';
import defaults from '../translations/en.json';

/** A gateway call that did not answer within REQUEST_TIMEOUT_MS. */
export class RequestTimeoutError extends Error {
  constructor() {
    super(defaults.serverNotResponding);
    this.name = 'RequestTimeoutError';
  }
}

/**
 * Rejects with RequestTimeoutError when `promise` takes longer than `ms`. The
 * request itself keeps running (fetch is not aborted); only the wait ends, so
 * no spinner or disabled control can outlive a gateway that stopped answering.
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number = REQUEST_TIMEOUT_MS,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new RequestTimeoutError()),
      ms,
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });

/**
 * Not timed: a turn can legitimately take minutes. The widget offers to cancel
 * it after SLOW_REPLY_MS instead.
 */
const UNTIMED = new Set<PropertyKey>(['sendPatientChatMessage']);

/**
 * The gateway client with every other async method bounded by withTimeout, so
 * a hung /start, /messages, /end or /cancel ends in an error the user can act
 * on instead of a frozen widget.
 */
export const withRequestTimeouts = (
  gateway: AgentGateway,
  ms: number = REQUEST_TIMEOUT_MS,
): AgentGateway =>
  new Proxy(gateway, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function' || UNTIMED.has(prop)) {
        return value;
      }
      return (...args: unknown[]) => {
        const result = value.apply(target, args);
        return result instanceof Promise ? withTimeout(result, ms) : result;
      };
    },
  });
